/**
 * Micro DOM toolkit with write-caching.
 *
 * The whole HUD runs at 60fps, so every mutation goes through a cell that
 * remembers the last written value and skips identical writes. That keeps
 * `update()` allocation-free-ish and, more importantly, produces zero
 * MutationRecords when nothing actually changed.
 *
 * Nothing in here reads layout (`offsetWidth`, `getBoundingClientRect`, ...),
 * so `update()` can never force a synchronous reflow.
 */

export interface ElOpts {
  cls?: string;
  text?: string;
  /** `data-testid` value. */
  tid?: string;
  attrs?: Readonly<Record<string, string>>;
  parent?: Node;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  o: ElOpts = {},
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (o.cls) node.className = o.cls;
  // textContent only — never innerHTML. Game data is never parsed as markup.
  if (o.text !== undefined) node.textContent = o.text;
  if (o.tid) node.setAttribute('data-testid', o.tid);
  if (o.attrs) {
    for (const k of Object.keys(o.attrs)) {
      const v = o.attrs[k];
      if (v !== undefined) node.setAttribute(k, v);
    }
  }
  if (o.parent) o.parent.appendChild(node);
  return node;
}

export function btn(o: ElOpts & { label?: string } = {}): HTMLButtonElement {
  const b = el('button', o);
  b.type = 'button';
  if (o.label) b.setAttribute('aria-label', o.label);
  return b;
}

/** Add a listener and get its disposer back. */
export function on(
  target: EventTarget,
  type: string,
  fn: EventListener,
  opts?: AddEventListenerOptions,
): () => void {
  target.addEventListener(type, fn, opts);
  return () => target.removeEventListener(type, fn, opts);
}

const UNSET = ' <unset>';

/** Cached `textContent` writer. */
export class Txt {
  private last = UNSET;
  constructor(readonly node: HTMLElement) {}
  set(v: string): void {
    if (v === this.last) return;
    this.last = v;
    this.node.textContent = v;
  }
}

/** Cached single-class toggle. */
export class Flag {
  private last: boolean | null = null;
  constructor(
    private readonly node: HTMLElement,
    private readonly cls: string,
  ) {}
  set(on_: boolean): void {
    if (on_ === this.last) return;
    this.last = on_;
    this.node.classList.toggle(this.cls, on_);
  }
}

/** Cached inline style property writer. */
export class Sty {
  private last = UNSET;
  constructor(
    private readonly node: HTMLElement,
    private readonly prop: string,
  ) {}
  set(v: string): void {
    if (v === this.last) return;
    this.last = v;
    this.node.style.setProperty(this.prop, v);
  }
}

/** Cached attribute writer. `null` removes the attribute. */
export class Attr {
  private last: string | null = UNSET;
  constructor(
    private readonly node: HTMLElement,
    private readonly name: string,
  ) {}
  set(v: string | null): void {
    if (v === this.last) return;
    this.last = v;
    if (v === null) this.node.removeAttribute(this.name);
    else this.node.setAttribute(this.name, v);
  }
}

/** Cached `hidden` toggle (uses the attribute, so `[hidden]` CSS applies). */
export class Hide {
  private last: boolean | null = null;
  constructor(private readonly node: HTMLElement) {}
  set(hidden: boolean): void {
    if (hidden === this.last) return;
    this.last = hidden;
    if (hidden) this.node.setAttribute('hidden', '');
    else this.node.removeAttribute('hidden');
  }
}

/** Cached `disabled` toggle for form controls. */
export class Dis {
  private last: boolean | null = null;
  constructor(private readonly node: HTMLButtonElement | HTMLInputElement) {}
  set(disabled: boolean): void {
    if (disabled === this.last) return;
    this.last = disabled;
    this.node.disabled = disabled;
  }
  get value(): boolean {
    return this.last ?? this.node.disabled;
  }
}

/**
 * Keyed child reconciler. Rows are built once and reused; the DOM is only
 * touched when the id list itself changes (a tier unlocks, an upgrade is
 * bought, an incident starts).
 */
export class KeyedRows<R> {
  readonly rows = new Map<string, R>();
  private order: string[] = [];

  constructor(
    private readonly host: HTMLElement,
    private readonly make: (id: string) => R,
    private readonly nodeOf: (r: R) => HTMLElement,
    private readonly dispose?: (r: R) => void,
  ) {}

  /** Returns true when the DOM was restructured. */
  sync(ids: readonly string[]): boolean {
    if (ids.length === this.order.length) {
      let same = true;
      for (let i = 0; i < ids.length; i++) {
        if (ids[i] !== this.order[i]) {
          same = false;
          break;
        }
      }
      if (same) return false;
    }

    const wanted = new Set(ids);
    for (const [id, row] of this.rows) {
      if (wanted.has(id)) continue;
      this.nodeOf(row).remove();
      this.dispose?.(row);
      this.rows.delete(id);
    }

    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      if (id === undefined) continue;
      let row = this.rows.get(id);
      if (row === undefined) {
        row = this.make(id);
        this.rows.set(id, row);
      }
      const node = this.nodeOf(row);
      if (this.host.childNodes[i] !== node) {
        this.host.insertBefore(node, this.host.childNodes[i] ?? null);
      }
    }

    this.order = ids.slice();
    return true;
  }

  clear(): void {
    for (const row of this.rows.values()) {
      this.nodeOf(row).remove();
      this.dispose?.(row);
    }
    this.rows.clear();
    this.order = [];
  }
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Focusable descendants, in tab order, skipping anything inside `[hidden]`. */
export function focusables(root: ParentNode): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (const node of root.querySelectorAll<HTMLElement>(FOCUSABLE)) {
    if (node.hasAttribute('disabled')) continue;
    if (node.closest('[hidden]') !== null) continue;
    if (node.getAttribute('aria-hidden') === 'true') continue;
    out.push(node);
  }
  return out;
}

/**
 * Keep Tab inside `container`: wrap forwards off the last focusable and
 * backwards off the first, and swallow Tab entirely when there is nothing to
 * focus. Returns true when the key was handled (and already prevented).
 *
 * Shared by `Modal` and the shop drawer so there is one trap in the codebase
 * rather than two that drift apart.
 */
export function trapTab(container: HTMLElement, e: KeyboardEvent): boolean {
  if (e.key !== 'Tab') return false;
  const items = focusables(container);
  if (items.length === 0) {
    e.preventDefault();
    return true;
  }
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement;
  if (e.shiftKey) {
    // Anything outside the container counts as "before the first": the trap has
    // to be able to pull focus back in, not just cycle it.
    if (active === first || active === container || !container.contains(active)) {
      e.preventDefault();
      last.focus();
      return true;
    }
  } else if (active === last) {
    e.preventDefault();
    first.focus();
    return true;
  }
  return false;
}

/** Cached-free `inert` toggle. Off the hot path — open/close and resize only. */
export function setInert(node: HTMLElement, on_: boolean): void {
  if (on_) node.setAttribute('inert', '');
  else node.removeAttribute('inert');
}

/** True when the event should be ignored by game hotkeys. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (target === null || !(target instanceof Element)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return (target as HTMLElement).isContentEditable === true;
}

/**
 * The *layout* viewport, in CSS pixels.
 *
 * Never use `window.innerWidth` for layout maths. That is the *visual*
 * viewport, and on mobile Chrome it expands to fit overflowing content — so
 * reading it while deciding how wide to draw creates a feedback loop, and
 * clamping an overlay against it permits positions past the real right edge.
 * `documentElement.clientWidth` stays honest.
 */
export function layoutViewport(): { vw: number; vh: number } {
  const doc = typeof document !== 'undefined' ? document.documentElement : null;
  const w = typeof window !== 'undefined' ? window : undefined;
  return {
    vw: doc?.clientWidth || w?.innerWidth || 0,
    vh: doc?.clientHeight || w?.innerHeight || 0,
  };
}
